import { ref } from 'vue'
import { apolloClient } from '@/shared/graphql/client'
import {
  NotificationsDocument,
  useMarkAllNotificationsAsReadMutation,
  useMarkNotificationAsReadMutation,
  type NotificationsQuery,
} from '@/shared/graphql/legacy/generated'

type ReadState = { id: string; read: boolean }

type NotificationsResponse = {
  notifications: ReadState[]
  unreadNotifications?: number | null
  next?: unknown
}

const readIds = ref(new Set<string>())
const visiblyUnreadIds = ref(new Set<string>())

const updateNotificationsCache = (
  update: (
    notifications: NotificationsQuery['notifications']
  ) => NotificationsQuery['notifications']
) =>
  apolloClient.cache.updateQuery<NotificationsQuery>(
    { query: NotificationsDocument },
    (response) => {
      if (!response) return

      return { ...response, notifications: update(response.notifications) }
    }
  )

export const isMarkedRead = (id: string) => readIds.value.has(id)

export const isNotificationRead = (notification: ReadState) =>
  notification.read || isMarkedRead(notification.id)

export const isVisiblyUnread = (id: string) => visiblyUnreadIds.value.has(id)

export const syncVisiblyUnread = (notifications: ReadState[]) =>
  notifications.forEach((notification) => {
    if (!isNotificationRead(notification)) {
      visiblyUnreadIds.value.add(notification.id)
    }
  })

export const resetVisiblyUnread = () => visiblyUnreadIds.value.clear()

export const unreadBadgeCount = (
  response: NotificationsResponse | null | undefined
) => {
  if (!response) return 0

  const staleReads = response.notifications.filter(
    (notification) => !notification.read && isMarkedRead(notification.id)
  ).length

  const count = Math.max(0, (response.unreadNotifications ?? 0) - staleReads)

  if (!count) return 0

  const drained =
    !response.next && response.notifications.every(isNotificationRead)

  return drained ? 0 : count
}

export const useNotificationsRead = () => {
  const { mutate: markAsRead } = useMarkNotificationAsReadMutation()
  const { mutate: markAllAsRead } = useMarkAllNotificationsAsReadMutation()

  const markRead = (candidateIds: string[]) => {
    const ids = candidateIds.filter((id) => !isMarkedRead(id))

    if (!ids.length) return

    ids.forEach((id) => readIds.value.add(id))
    markAsRead({ ids })
  }

  const markAllRead = () => {
    markAllAsRead()

    apolloClient.cache
      .readQuery<NotificationsQuery>({ query: NotificationsDocument })
      ?.notifications.notifications.forEach((notification) =>
        readIds.value.add(notification.id)
      )

    resetVisiblyUnread()

    updateNotificationsCache((notifications) => ({
      ...notifications,
      notifications: notifications.notifications.map((notification) => ({
        ...notification,
        read: true,
      })),
      unreadNotifications: 0,
    }))
  }

  return { markRead, markAllRead }
}
